import { useSearchParams } from 'react-router-dom'

import { useApiClient } from '@/api/ApiClientProvider'
import { competitionsCsvFilename, CSV_MAX_ROWS, type CompetitionRow } from '@/api/competitions'
import { SectionFrame } from '@/app/pages'
import type { RouteDef } from '@/app/routes'
import { Card, EmptyState, LoadingBlock } from '@/design'
import { useDrillDown, useMoney } from '@/features/overview/hooks'
import { usePermissions } from '@/lib/access'
import type { GlobalFilters } from '@/lib/filters'
import { usePageFreshness } from '@/lib/freshness'
import { useGlobalFilters } from '@/lib/useGlobalFilters'

import { CsvButton } from '../list/CsvButton'
import { ListTable } from '../list/ListTable'
import type { Paging } from '../list/ListStates'
import { COLUMN_CHOICE } from './columnChoice'
import { COMPETITION_COLUMNS, GROUP_LABELS, describeReason, type CellContext } from './columns'
import { CategoryFilter, StatusChips } from './Filters'
import { NoRows } from './ListStates'
import { unavailableReason } from './nullState'
import { useCompetitionCategories, useCompetitionsList } from './competitionsList'
import { categoryOptions, type CompetitionsResult } from './fold'
import { LIST_RETURN_PARAM, listReturnState, useCompetitionParams, useCompetitionsQuery, type CompetitionParams } from './params'
import '../list/list.css'
import './competitions.css'

/**
 * Analytics › Competitions: every competition in scope, one row each, with the
 * period's sales and measured behaviour (`GET /analytics/competitions`). The API
 * sorts and pages across every competition, so a header click asks it for a new
 * order rather than reordering the rows on screen, and "Show more" fetches the
 * next page. A row opens the competition's own page.
 */
export function CompetitionsPage({ route }: { route: RouteDef }) {
  return (
    <SectionFrame route={route}>
      <CompetitionsList />
    </SectionFrame>
  )
}

/** The list's request, its card, and its loading and outage states. */
function CompetitionsList() {
  const client = useApiClient()
  const { filters } = useGlobalFilters()
  const params = useCompetitionParams()
  const query = useCompetitionsQuery(filters, params)
  const list = useCompetitionsList(query)
  const { folded } = list
  const { can } = usePermissions()
  usePageFreshness(folded?.meta)
  // While the previous list stands in for the next, its period is not the one a download would cover.
  const period = list.isPlaceholderData ? undefined : folded?.meta.period

  return (
    <Card
      title="All competitions"
      subtitle={folded?.meta.period ? `${folded.meta.period.label} · sales and measured behaviour per competition` : undefined}
      busy={list.isFetching && !list.isFetchingNextPage}
      actions={
        can('exports.create') ? (
          <CsvButton
            key={JSON.stringify(query)}
            download={() => client.competitionsCsv(query)}
            filters={query.filters}
            period={period}
            filename={competitionsCsvFilename}
            noun="competitions"
            maxRows={CSV_MAX_ROWS}
          />
        ) : null
      }
    >
      {list.isPending ? (
        <LoadingBlock rows={6} label="Loading competitions" />
      ) : !folded ? (
        <EmptyState variant="outage" announce description="The competitions list could not be loaded just now. Try again in a moment." />
      ) : (
        <ListBody
          result={folded}
          filters={filters}
          params={params}
          paging={list.paging}
        />
      )}
    </Card>
  )
}

/** The loaded list: its filters around the shared server-sorted table (`../list/ListTable`). */
function ListBody({
  result,
  filters,
  params,
  paging,
}: {
  result: CompetitionsResult
  filters: GlobalFilters
  params: CompetitionParams
  paging: Paging
}) {
  const { rows, latest, notAvailable } = result
  const { locale } = useMoney(latest.currency)
  const href = useDrillDown()
  const [search] = useSearchParams()
  const listState = listReturnState(search)
  const categories = useCompetitionCategories(filters)
  const context: CellContext = { notAvailable, currency: latest.currency, locale, timezone: latest.timezone }
  const filtered = params.status !== undefined || params.category !== undefined
  const scoped = filters.dimensions.website !== undefined || filters.environments.length > 0

  return (
    <ListTable
      caption="Competitions"
      columns={COMPETITION_COLUMNS}
      groupLabels={GROUP_LABELS}
      columnChoice={COLUMN_CHOICE}
      sort={{ sort: params.sort, order: params.order }}
      onSort={params.setSort}
      rows={rows}
      rowKey={result.keyOf}
      rowHref={(row) => href(competitionDrillDown(row, listState))}
      context={context}
      rule={unavailableReason}
      describe={describeReason}
      // What "measured" means, in the API's own sentence, when a behaviour column is shown.
      lead={(visible) => (visible.some((column) => column.group === 'behaviour') ? latest.coverage.note : undefined)}
      toolbarStart={<StatusChips byStatus={latest.byStatus} selected={params.status} onSelect={params.setStatus} />}
      toolbarEnd={
        <CategoryFilter
          options={categoryOptions(categories.data?.categories, rows, params.category)}
          selected={params.category}
          partial={categories.isError || categories.data?.complete === false}
          onSelect={params.setCategory}
        />
      }
      empty={<NoRows filtered={filtered} scoped={scoped} onClear={params.clearFilters} />}
      footer={{ total: latest.total, noun: 'competitions', paging }}
    />
  )
}

/** The competition's page, carrying the list's own filters and sort so its back link returns to this view. */
function competitionDrillDown(row: CompetitionRow, listState: string) {
  const entity = { type: 'competition' as const, id: row.competitionId, label: row.name }
  return {
    path: `/analytics/competitions/${encodeURIComponent(row.competitionId)}`,
    entity,
    ...(listState ? { params: { [LIST_RETURN_PARAM]: listState } } : {}),
  }
}
